import React, { useState, useEffect, useCallback } from "react";
import axios from "../../../api/axios";
import { fetchProducts } from "../../../api/product";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Box,
} from "@mui/material";
import { TreeView, TreeItem } from "@mui/lab";
import {
  ChevronRightIcon,
  ArrowsPointingOutIcon,
  ArrowUpOnSquareIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { BuildingOfficeIcon, UserIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import { PMPagination } from "../PMPagination";
import { TWclassNames } from "../Div";
import { InfoListPopup } from "../infoListPopup";
import {
  findProductStatusByValue,
  findProductTypeById,
  findUnitById,
  findWarehouseStatusById,
} from "../../dashboard/products/product-data";
import { ProductLastUpdatedStatus } from "../../dashboard/products/utils";
import { PMDialog } from "../PMDialog";
import { PMField } from "../PMField";
import { debounce } from "lodash";
import { DocumentImage, documentSrc } from "../../dashboard/documents/utils";

export default function ItemSelector({ 
  multiple = true, 
  selectedItems = [], 
  testMode = false,
  ...props 
}) {
  const dispatch = useDispatch();
  const [state, setState] = useState({
    loading: false,
    error: false,
  });
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [currentCount, setCurrentCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchString, setSearchString] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(0); // Force re-render counter
  const [selectorAttributes, setSelectorAttributes] = useState({
    title: "",
    categorySelector: false,
    updateLink: null,
    getLink: null,
    getParam: null,
    getItemsLink: null,
    updateParams: [],
  });
  const { selectorType, open, module, onSelect } = props;
  const [currentNavSelect, setCurrentNavSelect] = useState(1);
  const [currentView, setCurrentView] = useState("all");
  const [params, setParams] = useState({
    filters: props.filters || [],
    fields:
      "id,displayTitle,title,item_number,brand,status,warehouse_status_type_id,unit_id,product_type,short_description,updated_at,purchase_price,sales_price,main_picture",
    page: 1,
    query: null, // Initialize as null, will be updated when searchString changes
    sort: "desc",
    sortBy: "updated_at",
    view: "all",
    ids: null,
    per_page: 10,
    show_inactive: false,
    [selectorType]: true,
  });
  // Close modal
  const handleClose = useCallback(() => {
    if (props.selectorCloseCallback) {
      props.selectorCloseCallback();
    }
  }, [props.selectorCloseCallback]);

  // Get the items (example: products) to fill the selector with.

  const getItems = useCallback(() => {
    const { getItemsLink } = selectorAttributes;

    if (!getItemsLink) {
      return;
    }

    setState({ loading: true, error: false });

    if (getItemsLink === "/products/list") {
      fetchProducts(params)
        .then((response) => {
          const newItems = response.data || [];
          setState({ loading: false, error: false });
          setItems(newItems);
          setCurrentCount(response.currentCount || 0);
          setTotalCount(response.count || 0);
          setForceUpdate(prev => prev + 1);
        })
        .catch((e) => {
          console.error("Error fetching products:", e);
          setState({ loading: false, error: true });
          toast.error("Could not fetch items for this selector. Error: " + e);
        });
    } else {
      // Try both relative and absolute paths
      const apiPath = getItemsLink.startsWith('/api/') ? getItemsLink : `/api/v1${getItemsLink}`;
      
      axios
        .get(apiPath, { params })
        .then((response) => {
          const newItems = response.data.data || [];
          setState({ loading: false, error: false });
          setItems(newItems);
          setCurrentCount(response.data.currentCount || 0);
          setTotalCount(response.data.count || 0);
          setForceUpdate(prev => prev + 1);
        })
        .catch((e) => {
          console.error("Error fetching items:", e);
          setState({ loading: false, error: true });
          toast.error("Could not fetch items for this selector. Error: " + e);
        });
    }
  }, [selectorAttributes, params]);

  // const getCategories = () => {
  //     const params = {
  //         parents: true
  //     }
  //     const fetchItems = async () => {
  //         axios.get(`/api/v1/products/categories/list?parents=true`, params)
  //         .then(response => {
  //             setState({loading: false, error: false});
  //             setCategories(response.data)
  //         }).catch(e => {
  //             setState({loading: false, error: true});
  //         });
  //     }
  //     toast.promise(
  //         fetchItems(),
  //         {
  //             loading: 'Laster inn kategorier',
  //             success: <b>Kategories ble lastet inn!</b>,
  //             error: <b>Kunne ikke laste inn kategorier</b>,
  //         }
  //     )
  // }

  // When we select a product, we add it to selectedProducts state.
  // If we uncheck a product, it will be removed from current state.
  const handleSelectProduct = (id) => {
    if (!id || isNaN(id)) {
      return;
    }

    const idToString = id.toString();

    if (multiple) {
      if (!selectedProducts.includes(idToString)) {
        setSelectedProducts((prev) => [...prev, idToString]);
      } else {
        const filteredSelectedProducts = selectedProducts.filter((value) => {
          return value !== idToString;
        });
        setSelectedProducts(filteredSelectedProducts);
      }
    } else {
      // For single selection, replace the entire array with just this item
      setSelectedProducts([idToString]);
      // Note: Auto-close is now handled by useEffect that watches selectedProducts
    }
  };

  // Checking if a product is selected in the selector.
  const checkIfChecked = (id) => {
    if (!id) return false;
    const idToString = id.toString();
    const isChecked = selectedProducts.length > 0 && selectedProducts.includes(idToString);
    return isChecked;
  };

  // Handle searching for products/ items
  // Fetches products depending on the searchString.
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchString(e.target.value);
  };

  // On mount, we will set the attributes for this selector.
  useEffect(() => {
    
    // This selector will be used for Product Attributes, Product Spare Parts and Product Categories.
    // Set attributes depending on selector type.

    // A selector for adding accessories to a product
    if (selectorType === "accessories") {
      setSelectorAttributes({
        title: "Select accessories for this product",
        categorySelector: true,
        updateLink: "/products/",
        showLink: "/dashboard/products/",
        getLink: "/products/",
        getItemsLink: "/products/list",
        getParam: "accessories",
      });
    } else if (selectorType === "spareParts") {
      // A selector for adding spare parts to a product
      setSelectorAttributes({
        title: "Select spare parts for this product",
        categorySelector: true,
        showLink: "/dashboard/products/",
        updateLink: "/products/",
        getLink: "/products/",
        getItemsLink: "/products/list",
        getParam: "spare_parts",
      });
    }
    // A selector for adding categories to a product
    else if (selectorType === "categories") {
      setSelectorAttributes({
        title: "Select categories for this product",
        categorySelector: false,
        showLink: "/dashboard/products/categories/",
        updateLink: "/products/",
        getLink: "/products/",
        getItemsLink: "/products/categories/list",
        getParam: "category_ids",
      });
    }
         // A selector for products that will be added to a order, report or other module..
     else if (selectorType === "products") {
       setSelectorAttributes({
         title: "Select products",
         categorySelector: true,
         updateLink: "/products/",
         showLink: "/dashboard/products/",
         getLink: "/products/",
         getItemsLink: "/products/list",
         getParam: "products",
       });
     } else {
       toast.error("No selector type was set for this selector");
     }
  }, [selectorType]);



  useEffect(() => {
    // Convert selectedItems to just IDs if they are objects
    const normalized = Array.isArray(selectedItems)
      ? selectedItems
      : selectedItems != null
      ? [selectedItems]
      : [];
    const selectedIds = normalized
      .map((item) => (item && typeof item === "object" ? item.id : item))
      .filter((id) => id != null)
      .map((id) => id.toString());
    setSelectedProducts(selectedIds);
  }, [selectedItems]);

  // Update params when searchString changes
  useEffect(() => {
    setParams((prevState) => ({
      ...prevState,
      query: searchString,
    }));
  }, [searchString]);

  // Auto-close for single selection when selectedProducts changes
  useEffect(() => {
    // Only auto-close for single selection when autoClose is enabled and we have selected products
    if (props.autoClose && !multiple && selectedProducts.length > 0) {
      // Small delay to ensure state is fully updated and items are loaded
      setTimeout(() => {
        // Only proceed if we have items loaded
        if (items && items.length > 0) {
          // For single selection, get the selected product and call onSelect
          if (onSelect) {
            const selectedId = parseInt(selectedProducts[0]);
            const selectedObject = items.find((object) => object.id === selectedId);
            
            if (selectedObject) {
              onSelect([selectedObject]);
            }
          }
          
          // Close the dialog
          handleClose();
        }
      }, 200);
    }
  }, [selectedProducts, props.autoClose, multiple, items, onSelect]);

  const debouncedDispatchAction = debounce((controller) => {
    getItems(controller);
  }, 300);



  // Single useEffect to handle all API calls
  useEffect(() => {
    // Only trigger API call when all conditions are met
    if (selectorType && open === true && selectorAttributes?.getItemsLink) {
      getItems();
    }

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [open, searchString, selectorAttributes]);

  // useEffect(() => {
  //     // Fetch selected items from module if attributes is set and if
  //     // selectedItems/ selectedProducts is not allready set.
  //     getModule();

  //     return () => {

  //     }

  // }, [selectorAttributes]);

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const tabs = [
    { id: 1, name: "All", icon: UserIcon, action: "all" },
    { id: 2, name: "Selected", icon: BuildingOfficeIcon, action: "selected" },
  ];

  const handleNavSelect = (id) => {
    const tab = tabs.find((tab) => tab.id === id);
    setCurrentNavSelect(id);
    setCurrentView(tab.action);
  };

  // Update params as currentView changes.
  useEffect(() => {
    switch (currentView) {
      case "all":
        setParams((prevState) => ({
          ...prevState,
          ids: null,
        }));
        return;
      case "selected":
        setParams((prevState) => ({
          ...prevState,
          ids: selectedProducts.join(","),
        }));
        return;
    }
  }, [currentView]);

  const handleQueryChange = (query) => {
    setParams((prevState) => ({
      ...prevState,
      query: query,
    }));
  };

  const handlePageChange = (page) => {
    setParams((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const handleInActiveChange = (boolean) => {
    setParams((prevState) => ({
      ...prevState,
      show_inactive: boolean,
    }));
  };

  const handleSave = () => {
    if (onSelect) {
      // For single selection, just get the first selected product
      if (!multiple && selectedProducts.length > 0) {
        const selectedId = parseInt(selectedProducts[0]);
        const selectedObject = items.find((object) => object.id === selectedId);

        if (selectedObject) {
          onSelect([selectedObject]);
          handleClose(); // Close the dialog after selection
        }
        return;
      }

      // For multiple selection, get all selected products
      const matchingIds = selectedProducts
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));
      
      const matchingObjects = items.filter((object) =>
        matchingIds.includes(object.id)
      );

      if (matchingObjects.length === 0) {
        return;
      }

      onSelect(matchingObjects);
      handleClose(); // Close the dialog after selection
      return;
    }

    if (!props.module_param) {
      toast.error("ID is not set? Cannot save");
      return;
    }

    if (!selectorAttributes.updateLink) {
      toast.error("updateLink is not set? Cannot save");
      return;
    }

    if (!selectorAttributes.getParam) {
      toast.error("getParam is not set? Cannot save");
      return;
    }

    const request = {
      product: {
        [selectorAttributes.getParam]: selectedProducts,
      },
    };

    const res = axios.put(
      `${selectorAttributes.updateLink}${props.module_param}`,
      request
    );

    toast.promise(res, {
      loading: "Lagrer....",
      success: (data) => {
        if (data.status === 500) throw new Error("Server error");
        return "Data was saved to " + module;
      },
      error: "Something went wrong...could not save!",
    });
  };

  const itemTree = (items) => {
    if (items && items.length > 0) {
      return items?.map((item) => {
        const productInfo = [
          { title: "Artikkelnummer", value: item.item_number },
          { title: "Tittel", value: item.title },
          { title: "Beskrivning", value: item.short_description },
          { title: "Märke", value: item?.brand?.title },
          {
            title: "Status",
            value: findProductStatusByValue(item?.status).label,
          },
          {
            title: "Produkt typ",
            value: findProductTypeById(item?.product_type).label,
          },
          {
            title: "Enhet",
            value: item?.unit_id
              ? findUnitById(item?.unit_id).label
              : "Ikke valgt",
          },
          {
            title: "Lagerstatus Type",
            value: item?.warehouse_status_type_id
              ? findWarehouseStatusById(item?.warehouse_status_type_id).label
              : "Ikke valgt",
          },
        ];

        return (
          <div
            key={item.id}
            className={`flex items-center p-4 border-b border-gray-200 hover:bg-blue-50 cursor-pointer transition-colors ${
              checkIfChecked(item.id) ? "bg-blue-100 border-blue-300" : ""
            }`}
            onClick={() => {
              handleSelectProduct(item.id);
            }}
          >
            <div className="flex-shrink-0 mr-4">
              <div className="w-16 h-16">
                <DocumentImage document={item?.main_picture || {}} />
              </div>
            </div>

            <div className="flex-grow min-w-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.title}
                  </p>
                  <p className="text-sm text-gray-500">
                    {item.item_number ? item.item_number : item.id}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Link
                    to={selectorAttributes.showLink + item.id}
                    target="_blank"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ArrowUpOnSquareIcon className="w-5 h-5" />
                  </Link>
                </div>
              </div>
              <div className="mt-2 space-y-1">
                <InfoListPopup info={productInfo}>
                  <span
                    className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View Details
                  </span>
                </InfoListPopup>
                <div className="text-xs text-gray-500">
                  <ProductLastUpdatedStatus product={item} />
                </div>
              </div>
            </div>
          </div>
        );
      });
    } else {
      return null;
    }
  };

  const navigation = () => {
    // Don't show navigation if there are no selected items
    if (!selectedItems || selectedItems.length === 0) {
      return null;
    }

    return (
      <div>
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            Select a tab
          </label>
          {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
          <select
            id="tabs"
            name="tabs"
            className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
            defaultValue={tabs.find((tab) => tab.id === currentNavSelect).name}
          >
            {tabs.map((tab) => (
              <option key={tab.name}>{tab.name}</option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => (
                <a
                  key={tab.name}
                  href={tab.href}
                  onClick={() => handleNavSelect(tab.id)}
                  className={classNames(
                    currentNavSelect === tab.id
                      ? "border-blue-400 text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                    "group inline-flex items-center py-4 px-1 border-b-2 font-medium text-xs"
                  )}
                  aria-current={
                    currentNavSelect === tab.id ? "page" : undefined
                  }
                >
                  <tab.icon
                    className={classNames(
                      currentNavSelect === tab.id
                        ? "text-blue-400"
                        : "text-gray-400 group-hover:text-gray-500",
                      "-ml-0.5 mr-2 h-5 w-5"
                    )}
                    aria-hidden="true"
                  />
                  <span>{tab.name}</span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const categoryTree = (categories) => {
    if (categories && categories.length > 0) {
      return categories?.map((category) => {
        // Return children of main category / descendants
        return (
          <TreeItem
            key={category.id}
            nodeId={category.id}
            label={category.title}
          >
            {/* Recursion  */}
            {/* Returning children's children of main category / descendants and so on...*/}
            {categoryTree(category.children)}
          </TreeItem>
        );
      });
    }
  };

  const renderProductCategories = () => {
    return (
      <Grid item xs={3}>
        <Grid item>
          <Box
            sx={{ height: 270, flexGrow: 1, maxWidth: 400, overflowY: "auto" }}
          >
            <TreeView
              aria-label="disabled items"
              defaultCollapseIcon={<ArrowsPointingOutIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
              // disabledItemsFocusable={}
            >
              {categoryTree(categories)}
            </TreeView>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const tableBody = () => {
    if (state.loading) {
      return (
        <div className="p-4 text-center text-gray-500">
          Loading items...
        </div>
      );
    } else if (state.error) {
      return (
        <div className="p-4 text-center text-red-500">
          Something went wrong...
        </div>
      );
    } else if (items && items.length > 0) {
      const renderedItems = itemTree(items);
      return <div className="divide-y divide-gray-200">{renderedItems}</div>;
    } else {
      return (
        <div className="p-4 text-center text-gray-500">
          <p>No items found...</p>
        </div>
      );
    }
  };

  return (
    <div key={`item-selector-${forceUpdate}`}>
             <PMDialog
         size={props.compact ? "4xl" : "7xl"}
         open={open}
         onClose={handleClose}
         title={selectorAttributes.title}
       >
         
        <div className="w-full">
          <div>
            <div>

              
              <form onSubmit={handleSearch} className="mt-10">
                <div className="relative rounded-md border border-gray-300 px-3 py-2 shadow-sm focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-400">
                  <label
                    htmlFor="name"
                    className="absolute -top-2 left-2 -mt-px inline-block bg-white px-1 text-xs font-medium text-gray-900"
                  >
                    Search
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="product-search"
                      id="product-search"
                      onChange={(e) => handleQueryChange(e.target.value)}
                      className="block w-full border-0 p-0 text-gray-900 placeholder-gray-500 focus:ring-0 sm:text-sm"
                      placeholder="Search...."
                    />
                  </div>
                </div>
              </form>

              <div className="py-5">
                <PMField
                  type="checkbox"
                  onChange={(e) => handleInActiveChange(e.target.checked)}
                  value={params.show_inactive}
                  name="show_inactive"
                  label="Show inactive"
                  {...params}
                />
              </div>
              {navigation()}

                             <div key={`table-body-${forceUpdate}`} className="mt-4 bg-white rounded-lg shadow overflow-hidden">
                 {tableBody()}
               </div>

              <PMPagination
                onPageChange={handlePageChange}
                page={params.page}
                perPage={params.per_page}
                rowsCount={currentCount}
                totalCount={totalCount}
                currentCount={currentCount}
                showCount={false}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-between my-5 mt-10">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2 bg-red-400 rounded-md shadow hover:bg-red-500 text-white"
          >
            Lukk
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2 bg-blue-400 rounded-md shadow hover:bg-blue-500 text-white"
          >
            Lagre
          </button>
        </div>
      </PMDialog>
    </div>
  );
}


